// Spawn + de-spawn (grab) ANIMATIONS for power-ups. The token look itself is unchanged
// (drawPowerUpToken with the chosen ?pu design); this wraps it with an entrance and an
// exit style: scale/rotate/offset transforms plus fx (rings, particles, sparkles). Pure
// canvas, PIXELS. `spawnP` 0->1 (0 = just spawned, 1 = fully in). `despawnP` 0->1 (0 =
// present, 1 = gone/grabbed). Switch in-game with ?pa=1..20.

import { drawPowerUpTokenFast, getPowerUpVariant } from "./powerup-designs";
import { readVariantParam } from "../shared/variant-param";

const TAU = Math.PI * 2;
const AFX: Record<string, string> = { self: "#22c55e", others: "#ff3b52", all: "#4f7bff" };

function rgba(hex: string, a: number): string {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
const fract = (x: number): number => x - Math.floor(x);
const rnd = (i: number): number => fract(Math.sin(i * 12.9898) * 43758.5453); // deterministic 0..1 per index
const easeOut = (p: number): number => 1 - Math.pow(1 - p, 3);
const easeOutBack = (p: number): number => { const c1 = 1.70158, c3 = c1 + 1; return 1 + c3 * Math.pow(p - 1, 3) + c1 * Math.pow(p - 1, 2); };
const easeOutElastic = (p: number): number => { if (p <= 0) return 0; if (p >= 1) return 1; const c4 = (2 * Math.PI) / 3; return Math.pow(2, -10 * p) * Math.sin((p * 10 - 0.75) * c4) + 1; };

function ring(ctx: CanvasRenderingContext2D, cx: number, cy: number, rad: number, lw: number, style: string): void {
  if (rad <= 0) return;
  ctx.lineWidth = Math.max(0.5, lw); ctx.strokeStyle = style; ctx.beginPath(); ctx.arc(cx, cy, rad, 0, TAU); ctx.stroke();
}
function burst(ctx: CanvasRenderingContext2D, cx: number, cy: number, baseR: number, prog: number, color: string, n: number): void {
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU, dist = baseR * (0.5 + easeOut(prog) * 2.0), rr = Math.max(0.5, baseR * 0.16 * (1 - prog));
    ctx.fillStyle = rgba(color, 1 - prog);
    ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, rr, 0, TAU); ctx.fill();
  }
}
function star(ctx: CanvasRenderingContext2D, x: number, y: number, rad: number, color: string): void {
  ctx.fillStyle = color; ctx.beginPath();
  for (let i = 0; i < 8; i++) { const rr = i % 2 ? rad * 0.4 : rad; const a = -Math.PI / 2 + (i * Math.PI) / 4; ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr); }
  ctx.closePath(); ctx.fill();
}
function sparkleBurst(ctx: CanvasRenderingContext2D, cx: number, cy: number, baseR: number, prog: number, color: string, n: number): void {
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU + i, dist = baseR * (0.4 + easeOut(prog) * 1.8), sr = Math.max(0.5, baseR * 0.3 * (1 - prog));
    star(ctx, cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, sr, rgba(color, 1 - prog));
  }
}

// --- curve / homing helpers for the directional grab styles ------------------
type Pt = [number, number];
function bez(x0: number, y0: number, cx: number, cy: number, x1: number, y1: number, t: number): Pt {
  const u = 1 - t; return [u * u * x0 + 2 * u * t * cx + t * t * x1, u * u * y0 + 2 * u * t * cy + t * t * y1];
}
// Control point offset perpendicular to the o->t line by `bend`, for an arcing path.
function ctrlPt(ox: number, oy: number, tx: number, ty: number, bend: number, along = 0.5): Pt {
  const mx = ox + (tx - ox) * along, my = oy + (ty - oy) * along, dx = tx - ox, dy = ty - oy, len = Math.hypot(dx, dy) || 1;
  return [mx - (dy / len) * bend, my + (dx / len) * bend];
}
// A curved comet (bezier arc) from origin to target, head at progress e, short trail.
function comet(ctx: CanvasRenderingContext2D, ox: number, oy: number, tx: number, ty: number, e: number, bend: number, tail: number, lw: number, style: string): void {
  const c = ctrlPt(ox, oy, tx, ty, bend);
  const t1 = e, t0 = Math.max(0, e - tail);
  ctx.lineCap = "round"; ctx.lineWidth = lw; ctx.strokeStyle = style;
  ctx.beginPath();
  const N = 7; for (let i = 0; i <= N; i++) { const p = bez(ox, oy, c[0], c[1], tx, ty, t0 + (t1 - t0) * (i / N)); if (i === 0) ctx.moveTo(p[0], p[1]); else ctx.lineTo(p[0], p[1]); }
  ctx.stroke();
  const h = bez(ox, oy, c[0], c[1], tx, ty, t1); ctx.fillStyle = style; ctx.beginPath(); ctx.arc(h[0], h[1], lw * 0.95, 0, TAU); ctx.fill();
}
// An S-shaped homing wiggle from origin to target.
function sComet(ctx: CanvasRenderingContext2D, ox: number, oy: number, tx: number, ty: number, e: number, amp: number, tail: number, lw: number, style: string): void {
  const dx = tx - ox, dy = ty - oy, len = Math.hypot(dx, dy) || 1, px = -dy / len, py = dx / len;
  const t1 = e, t0 = Math.max(0, e - tail);
  ctx.lineCap = "round"; ctx.lineWidth = lw; ctx.strokeStyle = style; ctx.beginPath();
  const N = 9; for (let i = 0; i <= N; i++) { const tt = t0 + (t1 - t0) * (i / N), off = Math.sin(tt * Math.PI * 2) * amp * (1 - tt); const bx = ox + dx * tt, by = oy + dy * tt; if (i === 0) ctx.moveTo(bx + px * off, by + py * off); else ctx.lineTo(bx + px * off, by + py * off); }
  ctx.stroke();
  const off1 = Math.sin(t1 * Math.PI * 2) * amp * (1 - t1); ctx.fillStyle = style; ctx.beginPath(); ctx.arc(ox + dx * t1 + px * off1, oy + dy * t1 + py * off1, lw * 0.95, 0, TAU); ctx.fill();
}
// Launches sideways then curves hard into the target (control near the start).
function sidewinder(ctx: CanvasRenderingContext2D, ox: number, oy: number, tx: number, ty: number, e: number, bend: number, tail: number, lw: number, style: string): void {
  const dx = tx - ox, dy = ty - oy, len = Math.hypot(dx, dy) || 1, px = -dy / len, py = dx / len;
  const c: Pt = [ox + px * bend + dx * 0.15, oy + py * bend + dy * 0.15];
  const t1 = e, t0 = Math.max(0, e - tail);
  ctx.lineCap = "round"; ctx.lineWidth = lw; ctx.strokeStyle = style; ctx.beginPath();
  const N = 8; for (let i = 0; i <= N; i++) { const p = bez(ox, oy, c[0], c[1], tx, ty, t0 + (t1 - t0) * (i / N)); if (i === 0) ctx.moveTo(p[0], p[1]); else ctx.lineTo(p[0], p[1]); }
  ctx.stroke();
  const h = bez(ox, oy, c[0], c[1], tx, ty, t1); ctx.fillStyle = style; ctx.beginPath(); ctx.arc(h[0], h[1], lw * 0.95, 0, TAU); ctx.fill();
}
// Green particles that curve from a ring around the token into the grabber.
function greenCurve(ctx: CanvasRenderingContext2D, cx: number, cy: number, tx: number, ty: number, r: number, e: number, style: string, n: number, size: number, bendScale: number): void {
  for (let i = 0; i < n; i++) { const a = (i / n) * TAU, sx = cx + Math.cos(a) * r * 1.2, sy = cy + Math.sin(a) * r * 1.2, bend = Math.sin(i * 1.7) * r * bendScale, c = ctrlPt(sx, sy, tx, ty, bend); const p = bez(sx, sy, c[0], c[1], tx, ty, e); ctx.fillStyle = style; ctx.beginPath(); ctx.arc(p[0], p[1], size, 0, TAU); ctx.fill(); }
}
// Green particles that spiral (vortex) into the grabber.
function greenVortex(ctx: CanvasRenderingContext2D, cx: number, cy: number, tx: number, ty: number, r: number, e: number, style: string, n: number, size: number): void {
  for (let i = 0; i < n; i++) { const base = (i / n) * TAU, sx = cx + Math.cos(base) * r * 1.2, sy = cy + Math.sin(base) * r * 1.2, lx = sx + (tx - sx) * e, ly = sy + (ty - sy) * e, spin = e * 5, rad = r * 0.55 * (1 - e); ctx.fillStyle = style; ctx.beginPath(); ctx.arc(lx + Math.cos(base + spin) * rad, ly + Math.sin(base + spin) * rad, size, 0, TAU); ctx.fill(); }
}

// A small, discrete projectile drawn at (x,y), pointing along `ang` (its travel
// direction). `sz` is a fixed pixel size so it stays the same at any distance.
function projectile(ctx: CanvasRenderingContext2D, x: number, y: number, ang: number, sz: number, shape: string, c: string): void {
  ctx.save(); ctx.translate(x, y); ctx.rotate(ang);
  if (shape === "orb") {
    const g = ctx.createRadialGradient(0, 0, 0, 0, 0, sz * 2.4);
    g.addColorStop(0, rgba(c, 0.9)); g.addColorStop(0.4, rgba(c, 0.5)); g.addColorStop(1, rgba(c, 0));
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, sz * 2.4, 0, TAU); ctx.fill();
    ctx.fillStyle = c; ctx.beginPath(); ctx.arc(0, 0, sz * 0.8, 0, TAU); ctx.fill();
  } else if (shape === "dart") {
    ctx.fillStyle = c; ctx.beginPath(); ctx.moveTo(sz * 1.7, 0); ctx.lineTo(-sz * 0.9, sz); ctx.lineTo(-sz * 0.35, 0); ctx.lineTo(-sz * 0.9, -sz); ctx.closePath(); ctx.fill();
  } else if (shape === "capsule") {
    ctx.lineCap = "round"; ctx.lineWidth = sz * 1.2; ctx.strokeStyle = c; ctx.beginPath(); ctx.moveTo(-sz * 1.1, 0); ctx.lineTo(sz * 1.1, 0); ctx.stroke();
    ctx.fillStyle = rgba("#ffffff", 0.85); ctx.beginPath(); ctx.arc(sz * 0.9, 0, sz * 0.32, 0, TAU); ctx.fill();
  } else if (shape === "drop") {
    ctx.fillStyle = c; ctx.beginPath(); ctx.arc(sz * 0.2, 0, sz, -Math.PI / 2, Math.PI / 2, false); ctx.lineTo(-sz * 1.9, 0); ctx.closePath(); ctx.fill();
  } else if (shape === "diamond") {
    ctx.fillStyle = c; ctx.beginPath(); ctx.moveTo(sz * 1.4, 0); ctx.lineTo(0, sz); ctx.lineTo(-sz * 1.4, 0); ctx.lineTo(0, -sz); ctx.closePath(); ctx.fill();
  } else if (shape === "star") {
    star(ctx, 0, 0, sz * 1.3, c);
  } else if (shape === "ring") {
    ctx.strokeStyle = c; ctx.lineWidth = Math.max(1, sz * 0.5); ctx.beginPath(); ctx.arc(0, 0, sz, 0, TAU); ctx.stroke();
  } else { // "ball"
    ctx.fillStyle = c; ctx.beginPath(); ctx.arc(0, 0, sz, 0, TAU); ctx.fill();
  }
  ctx.restore();
}
const PROJ_SHAPE: Record<number, string> = { 58: "ball", 59: "orb", 60: "dart", 61: "capsule", 62: "drop", 63: "diamond", 64: "star", 65: "ring" };

interface Tf { scale?: number; sx?: number; sy?: number; alpha?: number; rot?: number; dx?: number; dy?: number; }
function drawTok(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, image: any, affects: string, tv: number, t: number, o: Tf): void {
  const sx = o.sx != null ? o.sx : (o.scale != null ? o.scale : 1);
  const sy = o.sy != null ? o.sy : (o.scale != null ? o.scale : 1);
  const alpha = o.alpha != null ? o.alpha : 1;
  if (sx <= 0.001 || sy <= 0.001 || alpha <= 0.001) return;
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.translate(cx + (o.dx || 0), cy + (o.dy || 0));
  if (o.rot) ctx.rotate(o.rot);
  ctx.scale(sx, sy);
  ctx.translate(-cx, -cy);
  // Cached-base fast path for the shipped coin designs; identical output, a blit +
  // the per-frame FX instead of rebuilding every gradient each frame.
  drawPowerUpTokenFast(ctx, cx, cy, r, image, affects, tv, t);
  ctx.restore();
}

export const POWERUP_ANIMS: { n: number; label: string }[] = [
  { n: 1, label: "Pop (+ ring)" },
  { n: 2, label: "Fade" },
  { n: 3, label: "Squash bounce" },
  { n: 4, label: "Spin" },
  { n: 5, label: "Ripple" },
  { n: 6, label: "Sparkle" },
  { n: 7, label: "Balloon pop" },
  { n: 8, label: "Drop in / fall out" },
  { n: 9, label: "Particle burst" },
  { n: 10, label: "Flash" },
  { n: 11, label: "Iris (dot)" },
  { n: 12, label: "Shockwave" },
  { n: 13, label: "Confetti" },
  { n: 14, label: "Glow" },
  { n: 15, label: "Rise / lift out" },
  { n: 16, label: "Swirl" },
  { n: 17, label: "Zoom from huge" },
  { n: 18, label: "Elastic" },
  { n: 19, label: "Ring-in" },
  { n: 20, label: "Shatter" },
  // --- 10 variations of the particle burst (grab); spawn is a simple grow ---
  { n: 21, label: "Burst - dense dots" },
  { n: 22, label: "Burst - star sparks" },
  { n: 23, label: "Burst - streaks" },
  { n: 24, label: "Burst - confetti squares" },
  { n: 25, label: "Burst - expanding dot ring" },
  { n: 26, label: "Burst - gravity fountain" },
  { n: 27, label: "Burst - spiral" },
  { n: 28, label: "Burst - dust puff" },
  { n: 29, label: "Burst - firework (two-stage)" },
  { n: 30, label: "Burst - multi-colour" },
  // --- variations of the dust puff (28) ---
  { n: 31, label: "Dust puff - bigger & further" },
  { n: 32, label: "Dust puff - fewer large motes" },
  { n: 33, label: "Dust puff - fine mist" },
  { n: 34, label: "Dust puff - drifts up" },
  { n: 35, label: "Dust puff - + soft ring" },
  { n: 36, label: "Dust puff - lingering" },
  { n: 37, label: "Dust puff - uneven scatter" },
  { n: 38, label: "Dust puff - mixed sizes" },
  { n: 39, label: "Dust puff - swelling motes" },
  { n: 40, label: "Dust puff - wide cloud" },
  // --- directional grab: green sucked into grabber, red thrown at enemies ---
  { n: 41, label: "Directional - dots" },
  { n: 42, label: "Directional - comet streaks" },
  { n: 43, label: "Directional - subtle" },
  // --- variations of #42: curved streaks, creative sucks, homing red ---
  { n: 44, label: "Curved comets" },
  { n: 45, label: "Homing missiles (bright head)" },
  { n: 46, label: "Vortex suck + arc" },
  { n: 47, label: "Taffy pull + arc" },
  { n: 48, label: "S-curve homing" },
  { n: 49, label: "Comet trails" },
  { n: 50, label: "Wide arcs + vortex" },
  { n: 51, label: "Sidewinder (launch then home)" },
  { n: 52, label: "Sparkle wake" },
  { n: 53, label: "Gentle homing (subtle)" },
  // --- missile landing: farther debuffs take longer to arrive + impact ---
  { n: 54, label: "Missile landing (arc + impact)" },
  { n: 55, label: "Missile landing (low arc, trail)" },
  { n: 56, label: "Missile volley (staggered)" },
  { n: 57, label: "Missile landing (subtle)" },
  // --- discrete projectile shapes (same distance-timed travel + impact) ---
  { n: 58, label: "Projectile - ball" },
  { n: 59, label: "Projectile - glowing orb" },
  { n: 60, label: "Projectile - dart" },
  { n: 61, label: "Projectile - capsule missile" },
  { n: 62, label: "Projectile - water drop" },
  { n: 63, label: "Projectile - diamond" },
  { n: 64, label: "Projectile - star" },
  { n: 65, label: "Projectile - ring" },
  // --- #55 with shorter trails ---
  { n: 66, label: "Missile 55 - shorter trail" },
  { n: 67, label: "Missile 55 - short trail" },
  { n: 68, label: "Missile 55 - very short trail" },
  // the chosen combo
  { n: 69, label: "Combo: missile+cloud red / dust green+blue" },
];

// How long the de-spawn (grab) animation plays before the power-up is removed. The
// missile / projectile designs run longer so a far one has time to travel and land.
export function animDespawnDuration(variant: number): number {
  return (variant >= 54 && variant <= 69) ? 1.0 : 0.4;
}

export interface AnimTargets { selfTarget?: { x: number; y: number } | null; enemyTargets?: { x: number; y: number }[]; }

// Draw a power-up mid spawn/despawn. spawnP 0->1, despawnP 0->1. `opts` carries worm
// positions (PIXELS) for the directional grab styles: selfTarget = the grabbing worm's
// head (green sucks toward it), enemyTargets = the other worms (red throws dots at them).
export function drawPowerUpAnim(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
  image: any, affects: string,
  animVariant: number, t: number,
  spawnP: number, despawnP: number,
  opts?: AnimTargets,
): void {
  const s = Math.max(0, Math.min(1, spawnP));
  const d = Math.max(0, Math.min(1, despawnP));
  const col = AFX[affects] || AFX.all;
  const tv = getPowerUpVariant();
  const tok = (o: Tf) => drawTok(ctx, cx, cy, r, image, affects, tv, t, o);
  if (d <= 0 && s >= 1) { tok({}); return; } // idle: plain token

  switch (animVariant) {
    case 1: // Pop with an overshoot in, a shrink + ring on grab.
      if (d > 0) { tok({ scale: 1 - 0.25 * d, alpha: 1 - d }); ring(ctx, cx, cy, r * (1 + 1.2 * easeOut(d)), r * 0.14 * (1 - d), rgba(col, (1 - d) * 0.9)); }
      else tok({ scale: easeOutBack(s), alpha: Math.min(1, s * 2) });
      break;
    case 2: // Simple fade both ways.
      if (d > 0) tok({ alpha: 1 - d });
      else tok({ scale: 0.8 + 0.2 * easeOut(s), alpha: s });
      break;
    case 3: // Overshoot in; squash flat on grab.
      if (d > 0) tok({ sx: 1 + 0.4 * d, sy: 1 - d, alpha: 1 - d });
      else { const e = easeOutBack(s); tok({ sx: e, sy: e }); }
      break;
    case 4: // Spin in and out.
      if (d > 0) tok({ scale: 1 - d, rot: d * Math.PI, alpha: 1 - d });
      else tok({ scale: easeOut(s), rot: (1 - s) * -Math.PI });
      break;
    case 5: // Expanding ring accompanies both.
      if (d > 0) { tok({ scale: 1 - d, alpha: 1 - d }); ring(ctx, cx, cy, r * (1 + 1.6 * easeOut(d)), r * 0.16 * (1 - d), rgba(col, (1 - d) * 0.8)); }
      else { tok({ alpha: s, scale: 0.85 + 0.15 * s }); ring(ctx, cx, cy, r * (1 + s * 1.0), r * 0.14 * (1 - s), rgba(col, (1 - s) * 0.8)); }
      break;
    case 6: // Sparkles on entrance and grab.
      if (d > 0) { tok({ scale: 1 - d, alpha: 1 - d }); sparkleBurst(ctx, cx, cy, r, d, col, 7); }
      else { tok({ scale: easeOut(s) }); if (s < 1) sparkleBurst(ctx, cx, cy, r, s, "#ffffff", 6); }
      break;
    case 7: // Grow in; balloon up and fade on grab.
      if (d > 0) tok({ scale: 1 + 0.8 * easeOut(d), alpha: 1 - d });
      else tok({ scale: easeOut(s) });
      break;
    case 8: // Drops from above with a small bounce; falls out on grab.
      if (d > 0) tok({ dy: r * 3 * easeOut(d), alpha: 1 - d });
      else { const e = easeOutBack(s); tok({ dy: -r * 3 * (1 - e) }); }
      break;
    case 9: // Particles burst outward on grab.
      if (d > 0) { tok({ scale: 1 - d, alpha: 1 - d * 1.2 }); burst(ctx, cx, cy, r, d, col, 10); }
      else tok({ scale: easeOut(s) });
      break;
    case 10: // White flash on both.
      if (d > 0) { tok({ alpha: 1 - d }); ctx.save(); ctx.globalAlpha *= (1 - d) * 0.9; ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(cx, cy, r * (1 + 0.2 * d), 0, TAU); ctx.fill(); ctx.restore(); }
      else { tok({ scale: 0.9 + 0.1 * s }); if (s < 1) { ctx.save(); ctx.globalAlpha *= (1 - s) * 0.8; ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill(); ctx.restore(); } }
      break;
    case 11: // Grows from / collapses to a dot.
      if (d > 0) tok({ scale: 1 - easeOut(d) });
      else tok({ scale: easeOut(s) });
      break;
    case 12: // Big shockwave ring.
      if (d > 0) { tok({ scale: 1 - d, alpha: 1 - d }); ring(ctx, cx, cy, r * (1 + 2.2 * easeOut(d)), r * 0.2 * (1 - d), rgba(col, 1 - d)); }
      else tok({ scale: easeOut(s) });
      break;
    case 13: // Confetti bits scatter on grab.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const cols = [AFX.self, AFX.others, AFX.all, "#ffd23f"];
        for (let i = 0; i < 10; i++) { const a = (i / 10) * TAU + i * 2, dist = r * (0.4 + easeOut(d) * 2.2); const x = cx + Math.cos(a) * dist, y = cy + Math.sin(a) * dist + easeOut(d) * r * 1.5; ctx.fillStyle = rgba(cols[i % 4], 1 - d); ctx.fillRect(x - 1.5, y - 1.5, 3, 3); }
      } else tok({ scale: easeOut(s) });
      break;
    case 14: // A soft glow behind, flaring on grab.
      if (d > 0) { const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * (1.5 + d)); g.addColorStop(0, rgba(col, (1 - d) * 0.8)); g.addColorStop(1, rgba(col, 0)); ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, r * (1.5 + d), 0, TAU); ctx.fill(); tok({ alpha: 1 - d }); }
      else { const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 1.6); g.addColorStop(0, rgba(col, s * 0.6)); g.addColorStop(1, rgba(col, 0)); ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, r * 1.6, 0, TAU); ctx.fill(); tok({ alpha: s }); }
      break;
    case 15: // Rises up into place; lifts up and fades on grab.
      if (d > 0) tok({ dy: -r * 3 * easeOut(d), alpha: 1 - d });
      else tok({ dy: r * 3 * (1 - easeOut(s)), alpha: s });
      break;
    case 16: // Swirls in and out.
      if (d > 0) tok({ scale: 1 - d, rot: easeOut(d) * Math.PI * 1.5, alpha: 1 - d });
      else tok({ scale: easeOut(s), rot: (1 - easeOut(s)) * Math.PI * 1.5 });
      break;
    case 17: // Zooms down from huge on spawn.
      if (d > 0) tok({ scale: 1 - d });
      else tok({ scale: 2 - easeOut(s), alpha: s });
      break;
    case 18: // Elastic overshoot in; snap out.
      if (d > 0) tok({ scale: Math.max(0, 1 - easeOut(d)) });
      else tok({ scale: Math.max(0, easeOutElastic(s)) });
      break;
    case 19: // A ring contracts into the token on spawn, expands out on grab.
      if (d > 0) { tok({ alpha: 1 - d }); ring(ctx, cx, cy, r * (1 + 1.5 * easeOut(d)), r * 0.16 * (1 - d), rgba(col, 1 - d)); }
      else { tok({ alpha: s }); ring(ctx, cx, cy, r * (1 + (1 - s) * 1.4), r * 0.16, rgba(col, (1 - s) * 0.9)); }
      break;
    case 20: // Shatters into shards on grab.
      if (d > 0) {
        tok({ scale: Math.max(0, 1 - d * 1.5), alpha: 1 - d });
        for (let i = 0; i < 6; i++) { const a = (i / 6) * TAU, dist = r * (0.3 + easeOut(d) * 1.8); ctx.save(); ctx.globalAlpha *= 1 - d; ctx.translate(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist); ctx.rotate(a + d * 3); ctx.fillStyle = rgba(col, 0.9); ctx.fillRect(-r * 0.18, -r * 0.18, r * 0.36, r * 0.36); ctx.restore(); }
      } else tok({ scale: easeOut(s) });
      break;

    // ===== variations of the particle burst (9); spawn = simple grow =====
    case 21: // Dense cloud of small dots.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d * 1.3 });
        const n = 18; for (let i = 0; i < n; i++) { const a = (i / n) * TAU, dist = r * (0.5 + easeOut(d) * 2.4); ctx.fillStyle = rgba(col, 1 - d); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.5, r * 0.1 * (1 - d)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 22: // Star-shaped sparks.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 8; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + d, dist = r * (0.4 + easeOut(d) * 2.0); star(ctx, cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.5, r * 0.28 * (1 - d)), rgba(col, 1 - d)); }
      } else tok({ scale: easeOut(s) });
      break;
    case 23: // Radiating comet streaks.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 10, de = easeOut(d); ctx.lineCap = "round"; ctx.strokeStyle = rgba(col, 1 - d); ctx.lineWidth = Math.max(0.75, r * 0.12 * (1 - d));
        for (let i = 0; i < n; i++) { const a = (i / n) * TAU, i0 = r * (0.5 + de * 1.4), o1 = r * (0.7 + de * 2.6); ctx.beginPath(); ctx.moveTo(cx + Math.cos(a) * i0, cy + Math.sin(a) * i0); ctx.lineTo(cx + Math.cos(a) * o1, cy + Math.sin(a) * o1); ctx.stroke(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 24: // Spinning confetti squares.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 12; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + i, dist = r * (0.4 + easeOut(d) * 2.2); ctx.save(); ctx.globalAlpha *= 1 - d; ctx.translate(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist + easeOut(d) * r * 0.8); ctx.rotate(a + d * 4); ctx.fillStyle = col; ctx.fillRect(-r * 0.12, -r * 0.12, r * 0.24, r * 0.24); ctx.restore(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 25: // Dots held in a ring that expands.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 16, rad = r * (0.6 + easeOut(d) * 2.0); for (let i = 0; i < n; i++) { const a = (i / n) * TAU; ctx.fillStyle = rgba(col, 1 - d); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * rad, cy + Math.sin(a) * rad, Math.max(0.5, r * 0.09 * (1 - d)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 26: // Fountain: shoots up, arcs down under gravity.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 14; for (let i = 0; i < n; i++) { const ang = -Math.PI / 2 + (rnd(i) - 0.5) * 2.0, spd = (0.8 + rnd(i + 7) * 1.2) * r * 2.2; const x = cx + Math.cos(ang) * spd * d, y = cy + Math.sin(ang) * spd * d + 0.5 * r * 9 * d * d; ctx.fillStyle = rgba(col, 1 - d); ctx.beginPath(); ctx.arc(x, y, Math.max(0.5, r * 0.1 * (1 - d * 0.6)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 27: // Pinwheel spiral outward.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 14; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + easeOut(d) * 3.0, dist = r * (0.3 + easeOut(d) * 2.2); ctx.fillStyle = rgba(col, 1 - d); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.5, r * 0.09 * (1 - d)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 28: // Soft dust puff: many tiny, close, gentle.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 22; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + rnd(i) * 1.2, dist = r * (0.4 + easeOut(d) * 1.2); ctx.fillStyle = rgba(col, (1 - d) * 0.6); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.4, r * 0.06 * (1 - d)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 29: // Firework: a burst, then a second wave of white sparkles.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 10; for (let i = 0; i < n; i++) { const a = (i / n) * TAU, dist = r * (0.4 + easeOut(d) * 2.4); ctx.fillStyle = rgba(col, 1 - d); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.5, r * 0.1 * (1 - d)), 0, TAU); ctx.fill(); }
        if (d > 0.35) { const d2 = (d - 0.35) / 0.65; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + 0.3, dist = r * (0.4 + easeOut(d2) * 1.7); star(ctx, cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.4, r * 0.16 * (1 - d2)), rgba("#ffffff", 1 - d2)); } }
      } else tok({ scale: easeOut(s) });
      break;
    case 30: // Multi-colour pop.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const cols = [AFX.self, AFX.others, AFX.all, "#ffd23f", "#ffffff"], n = 15;
        for (let i = 0; i < n; i++) { const a = (i / n) * TAU, dist = r * (0.4 + easeOut(d) * 2.3); ctx.fillStyle = rgba(cols[i % 5], 1 - d); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.5, r * 0.1 * (1 - d)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;

    // ===== variations of the dust puff (28); spawn = simple grow =====
    case 31: // Same puff, popped a bit further out with slightly bigger particles.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 22; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + rnd(i) * 1.2, dist = r * (0.4 + easeOut(d) * 1.7); ctx.fillStyle = rgba(col, (1 - d) * 0.6); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.5, r * 0.09 * (1 - d)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 32: // Fewer, larger motes.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 12; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + rnd(i) * 1.5, dist = r * (0.4 + easeOut(d) * 1.4); ctx.fillStyle = rgba(col, (1 - d) * 0.7); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.6, r * 0.12 * (1 - d)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 33: // Fine, dense mist.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 32; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + rnd(i) * 1.0, dist = r * (0.3 + easeOut(d) * 1.0); ctx.fillStyle = rgba(col, (1 - d) * 0.5); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.4, r * 0.045 * (1 - d)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 34: // The puff drifts gently upward as it fades.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 20, rise = easeOut(d) * r * 0.9; for (let i = 0; i < n; i++) { const a = (i / n) * TAU, dist = r * (0.4 + easeOut(d) * 1.2); ctx.fillStyle = rgba(col, (1 - d) * 0.6); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist - rise, Math.max(0.5, r * 0.07 * (1 - d)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 35: // Puff plus a faint soft ring expanding out.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        ctx.lineWidth = Math.max(0.75, r * 0.12 * (1 - d)); ctx.strokeStyle = rgba(col, (1 - d) * 0.4); ctx.beginPath(); ctx.arc(cx, cy, r * (0.5 + easeOut(d) * 1.6), 0, TAU); ctx.stroke();
        const n = 18; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + rnd(i), dist = r * (0.4 + easeOut(d) * 1.3); ctx.fillStyle = rgba(col, (1 - d) * 0.55); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.5, r * 0.07 * (1 - d)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 36: // Lingering: spreads little but hangs in the air longer.
      if (d > 0) {
        const fade = Math.pow(1 - d, 0.6);
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 20; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + rnd(i) * 1.2, dist = r * (0.4 + easeOut(d) * 1.1); ctx.fillStyle = rgba(col, fade * 0.6); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.5, r * 0.08 * fade), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 37: // Uneven scatter: each mote flies its own distance and size.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 22; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + rnd(i) * 2, spd = 0.6 + rnd(i + 3) * 1.4, dist = r * (0.3 + easeOut(d) * spd); ctx.fillStyle = rgba(col, (1 - d) * 0.6); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.4, r * 0.07 * (1 - d) * (0.6 + rnd(i + 9) * 0.8)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 38: // A mix of big and small motes.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 24; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + rnd(i), dist = r * (0.4 + easeOut(d) * 1.3), big = i % 3 === 0; ctx.fillStyle = rgba(col, (1 - d) * (big ? 0.7 : 0.5)); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.4, (big ? r * 0.11 : r * 0.05) * (1 - d)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 39: // Motes swell mid-flight, then fade.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const swell = 0.4 + 0.6 * Math.sin(Math.min(1, d) * Math.PI), n = 18; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + rnd(i), dist = r * (0.4 + easeOut(d) * 1.3); ctx.fillStyle = rgba(col, (1 - d) * 0.6); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.5, r * 0.1 * swell), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    case 40: // A wide, gentle cloud.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 30; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + rnd(i) * 3, dist = r * (0.4 + easeOut(d) * 2.0); ctx.fillStyle = rgba(col, (1 - d) * 0.45); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.4, r * 0.05 * (1 - d)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;

    // ===== directional grab: green sucked into the grabber, red thrown at enemies =====
    case 41: { // Dots.
      if (d <= 0) { tok({ scale: easeOut(s) }); break; }
      const e = easeOut(d), self = opts && opts.selfTarget, enemies = (opts && opts.enemyTargets) || [];
      if (affects === "self") {
        const tx = self ? self.x : cx, ty = self ? self.y : cy;
        tok({ dx: (tx - cx) * e * 0.7, dy: (ty - cy) * e * 0.7, scale: 1 - d, alpha: 1 - d });
        const n = 12; for (let i = 0; i < n; i++) { const a = (i / n) * TAU, sx = cx + Math.cos(a) * r * 1.1, sy = cy + Math.sin(a) * r * 1.1; ctx.fillStyle = rgba(col, 1 - d); ctx.beginPath(); ctx.arc(sx + (tx - sx) * e, sy + (ty - sy) * e, Math.max(0.5, r * 0.09 * (1 - d * 0.4)), 0, TAU); ctx.fill(); }
      } else if (affects === "others" && enemies.length) {
        tok({ scale: 1 - d, alpha: 1 - d });
        for (const en of enemies) for (let k = 0; k < 3; k++) { const ek = easeOut(Math.max(0, Math.min(1, d * 1.2 - k * 0.12))); ctx.fillStyle = rgba(col, 1 - d); ctx.beginPath(); ctx.arc(cx + (en.x - cx) * ek, cy + (en.y - cy) * ek, Math.max(0.5, r * 0.1 * (1 - ek * 0.5)), 0, TAU); ctx.fill(); }
      } else { tok({ scale: 1 - d, alpha: 1 - d }); const n = 12; for (let i = 0; i < n; i++) { const a = (i / n) * TAU, dist = r * (0.5 + e * 2.0); ctx.fillStyle = rgba(col, 1 - d); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.5, r * 0.1 * (1 - d)), 0, TAU); ctx.fill(); } }
      break;
    }
    case 42: { // Comet streaks.
      if (d <= 0) { tok({ scale: easeOut(s) }); break; }
      const e = easeOut(d), self = opts && opts.selfTarget, enemies = (opts && opts.enemyTargets) || [];
      ctx.lineCap = "round"; ctx.lineWidth = Math.max(0.75, r * 0.1 * (1 - d)); ctx.strokeStyle = rgba(col, 1 - d);
      if (affects === "self") {
        const tx = self ? self.x : cx, ty = self ? self.y : cy;
        tok({ dx: (tx - cx) * e * 0.7, dy: (ty - cy) * e * 0.7, scale: 1 - d, alpha: 1 - d });
        const n = 10; for (let i = 0; i < n; i++) { const a = (i / n) * TAU, sx = cx + Math.cos(a) * r * 1.2, sy = cy + Math.sin(a) * r * 1.2, e2 = Math.max(0, e - 0.18); ctx.beginPath(); ctx.moveTo(sx + (tx - sx) * e2, sy + (ty - sy) * e2); ctx.lineTo(sx + (tx - sx) * e, sy + (ty - sy) * e); ctx.stroke(); }
      } else if (affects === "others" && enemies.length) {
        tok({ scale: 1 - d, alpha: 1 - d });
        for (const en of enemies) { const e2 = Math.max(0, e - 0.2); ctx.beginPath(); ctx.moveTo(cx + (en.x - cx) * e2, cy + (en.y - cy) * e2); ctx.lineTo(cx + (en.x - cx) * e, cy + (en.y - cy) * e); ctx.stroke(); }
      } else { tok({ scale: 1 - d, alpha: 1 - d }); const n = 10; for (let i = 0; i < n; i++) { const a = (i / n) * TAU, i0 = r * (0.5 + e * 1.2), o1 = r * (0.7 + e * 2.2); ctx.beginPath(); ctx.moveTo(cx + Math.cos(a) * i0, cy + Math.sin(a) * i0); ctx.lineTo(cx + Math.cos(a) * o1, cy + Math.sin(a) * o1); ctx.stroke(); } }
      break;
    }
    case 43: { // Subtle directional (small, dusty dots).
      if (d <= 0) { tok({ scale: easeOut(s) }); break; }
      const e = easeOut(d), self = opts && opts.selfTarget, enemies = (opts && opts.enemyTargets) || [];
      if (affects === "self") {
        const tx = self ? self.x : cx, ty = self ? self.y : cy;
        tok({ dx: (tx - cx) * e * 0.6, dy: (ty - cy) * e * 0.6, scale: 1 - d, alpha: 1 - d });
        const n = 10; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + rnd(i), sx = cx + Math.cos(a) * r, sy = cy + Math.sin(a) * r; ctx.fillStyle = rgba(col, (1 - d) * 0.7); ctx.beginPath(); ctx.arc(sx + (tx - sx) * e, sy + (ty - sy) * e, Math.max(0.4, r * 0.06 * (1 - d)), 0, TAU); ctx.fill(); }
      } else if (affects === "others" && enemies.length) {
        tok({ scale: 1 - d, alpha: 1 - d });
        for (const en of enemies) for (let k = 0; k < 2; k++) { const ek = easeOut(Math.max(0, Math.min(1, d * 1.2 - k * 0.14))); ctx.fillStyle = rgba(col, (1 - d) * 0.8); ctx.beginPath(); ctx.arc(cx + (en.x - cx) * ek, cy + (en.y - cy) * ek, Math.max(0.4, r * 0.07 * (1 - ek * 0.5)), 0, TAU); ctx.fill(); }
      } else { tok({ scale: 1 - d, alpha: 1 - d }); const n = 16; for (let i = 0; i < n; i++) { const a = (i / n) * TAU, dist = r * (0.4 + e * 1.4); ctx.fillStyle = rgba(col, (1 - d) * 0.6); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.4, r * 0.06 * (1 - d)), 0, TAU); ctx.fill(); } }
      break;
    }

    // ===== variations of #42: curved streaks + creative sucks + homing red =====
    // Shared spawn = grow; green sucks toward the grabber, red homes at each enemy.
    case 44: case 45: case 46: case 47: case 48: case 49: case 50: case 51: case 52: case 53: {
      if (d <= 0) { tok({ scale: easeOut(s) }); break; }
      const e = easeOut(d), self = opts && opts.selfTarget, enemies = (opts && opts.enemyTargets) || [];
      const cc = rgba(col, 1 - d), lw = Math.max(0.75, r * 0.1 * (1 - d));
      // per-design knobs: [greenStyle, redStyle, bend, tail, alphaMul]
      const V = animVariant;
      if (affects === "self") {
        const tx = self ? self.x : cx, ty = self ? self.y : cy;
        const pull = V === 47 ? 0.55 : 0.7; // taffy pulls a bit less (it stretches instead)
        tok({ dx: (tx - cx) * e * pull, dy: (ty - cy) * e * pull, scale: 1 - d, alpha: 1 - d });
        if (V === 46 || V === 50) greenVortex(ctx, cx, cy, tx, ty, r, e, cc, 12, Math.max(0.5, r * 0.08 * (1 - d)));
        else if (V === 47) { // taffy: a thick smear stretching toward the grabber
          ctx.lineCap = "round"; ctx.lineWidth = r * 0.7 * (1 - d); ctx.strokeStyle = rgba(col, (1 - d) * 0.5);
          ctx.beginPath(); ctx.moveTo(cx + (tx - cx) * e * 0.3, cy + (ty - cy) * e * 0.3); ctx.lineTo(cx + (tx - cx) * e, cy + (ty - cy) * e); ctx.stroke();
        } else if (V === 52) { greenCurve(ctx, cx, cy, tx, ty, r, e, cc, 9, Math.max(0.5, r * 0.07 * (1 - d)), 1.4); for (let i = 0; i < 5; i++) { const a = (i / 5) * TAU, sx = cx + Math.cos(a) * r, sy = cy + Math.sin(a) * r; star(ctx, sx + (tx - sx) * e, sy + (ty - sy) * e, Math.max(0.5, r * 0.12 * (1 - d)), rgba("#ffffff", 1 - d)); } }
        else greenCurve(ctx, cx, cy, tx, ty, r, e, cc, 10, Math.max(0.5, r * 0.08 * (1 - d)), V === 53 ? 0.7 : 1.4);
      } else if (affects === "others" && enemies.length) {
        tok({ scale: 1 - d, alpha: 1 - d });
        enemies.forEach((en, ei) => {
          const dir = ei % 2 ? 1 : -1, dist = Math.hypot(en.x - cx, en.y - cy), bend = dir * dist * (V === 50 ? 0.35 : V === 53 ? 0.08 : 0.2);
          if (V === 48) sComet(ctx, cx, cy, en.x, en.y, e, dist * 0.14, 0.28, lw, cc);
          else if (V === 51) sidewinder(ctx, cx, cy, en.x, en.y, e, dir * dist * 0.4, 0.28, lw, cc);
          else if (V === 45) { comet(ctx, cx, cy, en.x, en.y, e, bend, 0.4, lw * 1.2, cc); const c2 = ctrlPt(cx, cy, en.x, en.y, bend), h = bez(cx, cy, c2[0], c2[1], en.x, en.y, e); ctx.fillStyle = rgba("#ffffff", 1 - d); ctx.beginPath(); ctx.arc(h[0], h[1], lw * 0.7, 0, TAU); ctx.fill(); }
          else if (V === 49) { comet(ctx, cx, cy, en.x, en.y, e, bend, 0.5, lw, cc); const c2 = ctrlPt(cx, cy, en.x, en.y, bend); for (let k = 1; k <= 4; k++) { const p = bez(cx, cy, c2[0], c2[1], en.x, en.y, Math.max(0, e - k * 0.12)); ctx.fillStyle = rgba(col, (1 - d) * (1 - k / 5)); ctx.beginPath(); ctx.arc(p[0], p[1], lw * 0.5, 0, TAU); ctx.fill(); } }
          else if (V === 52) { comet(ctx, cx, cy, en.x, en.y, e, bend, 0.24, lw, cc); const c2 = ctrlPt(cx, cy, en.x, en.y, bend); for (let k = 1; k <= 3; k++) { const p = bez(cx, cy, c2[0], c2[1], en.x, en.y, Math.max(0, e - k * 0.16)); star(ctx, p[0], p[1], Math.max(0.5, r * 0.1 * (1 - d)), rgba("#ffffff", (1 - d) * (1 - k / 4))); } }
          else comet(ctx, cx, cy, en.x, en.y, e, bend, V === 53 ? 0.18 : 0.24, lw, cc); // 44, 46, 47, 50, 53
        });
      } else { // blue / no targets: outward curved-ish burst
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 10; ctx.lineCap = "round"; ctx.lineWidth = lw; ctx.strokeStyle = cc;
        for (let i = 0; i < n; i++) { const a = (i / n) * TAU, i0 = r * (0.5 + e * 1.2), o1 = r * (0.7 + e * 2.2); ctx.beginPath(); ctx.moveTo(cx + Math.cos(a) * i0, cy + Math.sin(a) * i0); ctx.lineTo(cx + Math.cos(a) * o1, cy + Math.sin(a) * o1); ctx.stroke(); }
      }
      break;
    }

    // ===== missile landing: red travel time scales with distance, impact on landing =====
    // 54-57 draw a line/comet; 58-65 draw a discrete projectile SHAPE (ball/dart/drop/...).
    case 54: case 55: case 56: case 57:
    case 58: case 59: case 60: case 61: case 62: case 63: case 64: case 65:
    case 66: case 67: case 68: {
      if (d <= 0) { tok({ scale: easeOut(s) }); break; }
      const V = animVariant, self = opts && opts.selfTarget, enemies = (opts && opts.enemyTargets) || [];
      if (affects === "self") { // green is local: quick suck-in in the first part of the window
        const tx = self ? self.x : cx, ty = self ? self.y : cy;
        const gp = Math.min(1, d / 0.4), ge = easeOut(gp);
        tok({ dx: (tx - cx) * ge * 0.75, dy: (ty - cy) * ge * 0.75, scale: 1 - gp, alpha: 1 - gp });
        if (gp < 1) greenVortex(ctx, cx, cy, tx, ty, r, ge, rgba(col, 1 - gp), 12, Math.max(0.5, r * 0.08 * (1 - gp)));
      } else if (affects === "others" && enemies.length) {
        const tf = Math.min(1, d / 0.3); // token vanishes fast; the missiles keep flying
        if (tf < 1) tok({ scale: 1 - tf, alpha: 1 - tf });
        let maxDist = 1; for (const en of enemies) { const dd = Math.hypot(en.x - cx, en.y - cy); if (dd > maxDist) maxDist = dd; }
        const lw = Math.max(0.75, r * 0.1);
        enemies.forEach((en, ei) => {
          const dist = Math.hypot(en.x - cx, en.y - cy) || 1;
          const travel = 0.6 * (dist / maxDist);              // farther = longer flight (missile landing)
          const stagger = V === 56 ? ei * 0.05 : 0;
          const landAt = stagger + travel;
          const prog = Math.max(0, Math.min(1, (d - stagger) / Math.max(0.05, travel)));
          const lowArc = (V === 55 || (V >= 66 && V <= 68)); // #55 and its shorter-trail siblings
          const dir = ei % 2 ? 1 : -1, bend = dir * dist * (V === 57 ? 0.08 : lowArc ? 0.12 : 0.22);
          // Fixed-PIXEL trail so the missile is the same size no matter how far it flies
          // (tail is a bezier-t fraction, so divide the target pixel length by the distance).
          const trailPx = r * (V === 55 ? 2.6 : V === 66 ? 2.0 : V === 67 ? 1.5 : V === 68 ? 1.1 : V === 57 ? 1.0 : 1.5);
          const tail = Math.min(0.9, trailPx / dist);
          if (prog < 1) {
            if (V <= 57 || (V >= 66 && V <= 68)) { // line / comet
              comet(ctx, cx, cy, en.x, en.y, prog, bend, tail, V === 57 ? lw * 0.75 : lw, rgba(col, 1));
              if (V === 54 || V === 55 || (V >= 66 && V <= 68)) { const c2 = ctrlPt(cx, cy, en.x, en.y, bend), h = bez(cx, cy, c2[0], c2[1], en.x, en.y, prog); ctx.fillStyle = rgba("#ffffff", 0.9); ctx.beginPath(); ctx.arc(h[0], h[1], lw * 0.7, 0, TAU); ctx.fill(); }
            } else { // discrete projectile shape, oriented along travel + a short motion trail
              const c2 = ctrlPt(cx, cy, en.x, en.y, bend);
              const h = bez(cx, cy, c2[0], c2[1], en.x, en.y, prog), hb = bez(cx, cy, c2[0], c2[1], en.x, en.y, Math.max(0, prog - 0.05));
              const ang = Math.atan2(h[1] - hb[1], h[0] - hb[0]), sz = r * 0.34;
              for (let k = 2; k >= 1; k--) { const p = bez(cx, cy, c2[0], c2[1], en.x, en.y, Math.max(0, prog - k * 0.03)); ctx.fillStyle = rgba(col, 0.35 / k); ctx.beginPath(); ctx.arc(p[0], p[1], sz * 0.5, 0, TAU); ctx.fill(); }
              projectile(ctx, h[0], h[1], ang, sz, PROJ_SHAPE[V] || "ball", col);
            }
          } else { // impact: ring + burst at the enemy
            const ip = Math.min(1, (d - landAt) / 0.3);
            ctx.strokeStyle = rgba(col, (1 - ip) * 0.9); ctx.lineWidth = Math.max(0.75, r * 0.14 * (1 - ip));
            ctx.beginPath(); ctx.arc(en.x, en.y, r * (0.2 + ip * 1.0), 0, TAU); ctx.stroke();
            const nb = V === 57 ? 4 : 6; for (let k = 0; k < nb; k++) { const a = (k / nb) * TAU, dd = r * (0.2 + ip * 0.9); ctx.fillStyle = rgba(col, 1 - ip); ctx.beginPath(); ctx.arc(en.x + Math.cos(a) * dd, en.y + Math.sin(a) * dd, Math.max(0.5, r * 0.09 * (1 - ip)), 0, TAU); ctx.fill(); }
          }
        });
      } else { tok({ scale: 1 - d, alpha: 1 - d }); }
      break;
    }

    case 69: { // Combo: red = a #40 dust cloud at the pickup + missiles #68 to each enemy;
      // green + blue = dust ring (#35). (Green's black-hole suck is parked for later.)
      if (d <= 0) { tok({ scale: easeOut(s) }); break; }
      const enemies = (opts && opts.enemyTargets) || [];
      if (affects === "others" && enemies.length) {
        const tf = Math.min(1, d / 0.3); if (tf < 1) tok({ scale: 1 - tf, alpha: 1 - tf });
        // #40 wide dust cloud at the pickup spot (quick), on top of which the missiles launch
        const cd = Math.min(1, d / 0.4);
        if (cd < 1) { for (let i = 0; i < 30; i++) { const a = (i / 30) * TAU + rnd(i) * 3, dist = r * (0.4 + easeOut(cd) * 2.0); ctx.fillStyle = rgba(col, (1 - cd) * 0.45); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.4, r * 0.05 * (1 - cd)), 0, TAU); ctx.fill(); } }
        let maxDist = 1; for (const en of enemies) { const dd = Math.hypot(en.x - cx, en.y - cy); if (dd > maxDist) maxDist = dd; }
        const lw = Math.max(0.75, r * 0.1);
        enemies.forEach((en, ei) => {
          const dist = Math.hypot(en.x - cx, en.y - cy) || 1, travel = 0.6 * (dist / maxDist), landAt = travel, prog = Math.max(0, Math.min(1, d / Math.max(0.05, travel)));
          const dir = ei % 2 ? 1 : -1, bend = dir * dist * 0.12, tail = Math.min(0.9, (r * 1.1) / dist);
          if (prog < 1) {
            comet(ctx, cx, cy, en.x, en.y, prog, bend, tail, lw, rgba(col, 1));
            const c2 = ctrlPt(cx, cy, en.x, en.y, bend), h = bez(cx, cy, c2[0], c2[1], en.x, en.y, prog); ctx.fillStyle = rgba("#ffffff", 0.9); ctx.beginPath(); ctx.arc(h[0], h[1], lw * 0.7, 0, TAU); ctx.fill();
          } else {
            const ip = Math.min(1, (d - landAt) / 0.3);
            ctx.strokeStyle = rgba(col, (1 - ip) * 0.9); ctx.lineWidth = Math.max(0.75, r * 0.14 * (1 - ip)); ctx.beginPath(); ctx.arc(en.x, en.y, r * (0.2 + ip * 1.0), 0, TAU); ctx.stroke();
            for (let k = 0; k < 6; k++) { const a = (k / 6) * TAU, dd = r * (0.2 + ip * 0.9); ctx.fillStyle = rgba(col, 1 - ip); ctx.beginPath(); ctx.arc(en.x + Math.cos(a) * dd, en.y + Math.sin(a) * dd, Math.max(0.5, r * 0.09 * (1 - ip)), 0, TAU); ctx.fill(); }
          }
        });
      } else { // green (self) + blue (all): dust puff + soft ring, quick
        const bd = Math.min(1, d / 0.4);
        tok({ scale: 1 - bd, alpha: 1 - bd });
        ctx.lineWidth = Math.max(0.75, r * 0.12 * (1 - bd)); ctx.strokeStyle = rgba(col, (1 - bd) * 0.4); ctx.beginPath(); ctx.arc(cx, cy, r * (0.5 + easeOut(bd) * 1.6), 0, TAU); ctx.stroke();
        const n = 18; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + rnd(i), dist = r * (0.4 + easeOut(bd) * 1.3); ctx.fillStyle = rgba(col, (1 - bd) * 0.55); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.5, r * 0.07 * (1 - bd)), 0, TAU); ctx.fill(); }
      }
      break;
    }

    default: { // fall back to the dust puff for any unknown variant.
      if (d > 0) {
        tok({ scale: 1 - d, alpha: 1 - d });
        const n = 22; for (let i = 0; i < n; i++) { const a = (i / n) * TAU + rnd(i) * 1.2, dist = r * (0.4 + easeOut(d) * 1.2); ctx.fillStyle = rgba(col, (1 - d) * 0.6); ctx.beginPath(); ctx.arc(cx + Math.cos(a) * dist, cy + Math.sin(a) * dist, Math.max(0.4, r * 0.06 * (1 - d)), 0, TAU); ctx.fill(); }
      } else tok({ scale: easeOut(s) });
      break;
    }
  }
}

// Which spawn/despawn animation to use IN-GAME. ?pa=1..69 (persists); default 69 (the combo:
// missile #68 for red, dust ring for green + blue).
const OK = (s: string | null): boolean => !!s && /^([1-9]|[1-5][0-9]|6[0-9])$/.test(s);
export function getPowerUpAnimVariant(): number {
  return readVariantParam("pa", "paVariant", OK, 69);
}
// Capture ?pa at module load (persists to localStorage) so it applies even when the URL is
// stripped by navigation before the first power-up is ever drawn.
getPowerUpAnimVariant();
